import { PreviewAccessRequestModel, useCurrentUser } from '@capturi/core'
import {
  PermissionPresetButton,
  PreviewAccessWarningText,
  ValidationErrorText,
} from '@capturi/sharing'
import { Box, Flex, Icon, Text, VStack, useToast } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import { FC, useCallback, useContext, useEffect } from 'react'
import isEqual from 'react-fast-compare'
import { useNavigate, useParams } from 'react-router'

import { Button } from '@capturi/ui-components'
import { useModal } from '@capturi/use-modal'
import { IoMdEye } from 'react-icons/io'

import { useOrganization, useUsers } from '@capturi/stores'
import { i18n } from '@lingui/core'
import { PermissionDialogWithOptions } from '../../components/PermissionDialog'
import {
  QuestionnairePreviewModal,
  usePublishQuestionnaire,
  useQuestionnaire,
  useUpdateQuestionnaire,
} from '../../features/questionnaire'
import {
  EditorStoreProvider,
  QuestionnaireEditorStoreContext,
  useEditorStore,
} from './EditorStoreProvider'
import { ConfirmCancelDialog } from './components/ConfirmCancelDialog'
import { QuestionnaireActionsMenu } from './components/QuestionnaireActionsMenu'
import { QuestionnaireEditor } from './components/QuestionnaireEditor'
import { QuestionnaireTitle } from './components/QuestionnaireTitle'
import { routes } from './routes'
import { getSharingOptions } from './sharing/getSharingOptions'
import { useQuestionnairePreviewAccess } from './sharing/useQuestionnairePreviewAccess'
import { EditorStoreState } from './useQuestionnaireEditorStore'

const select = (state: EditorStoreState) => ({
  canPublish: state.canPublish(),
  hasTitle: !!state.questionnaire.title.trim(),
  loadQuestionnaire: state.loadQuestionnaire,
  permissionPreset: state.questionnaire.permissionPreset,
  permissionPresetUserUid: state.questionnaire.permissionPresetUserUid,
  createdByUserUid: state.questionnaire.createdByUserUid,
  updatedByUserUid: state.questionnaire.updatedByUserUid,
  created: state.questionnaire.created,
  updated: state.questionnaire.updated,
  acl: state.questionnaire.acl,
})

const QAQuestionnaireEditPageContent: FC = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const { uid } = useParams<{ uid: string }>()
  const [openConfirmDialog] = useModal(ConfirmCancelDialog)

  const {
    canPublish,
    loadQuestionnaire,
    hasTitle,
    permissionPreset,
    permissionPresetUserUid,
    acl,
    createdByUserUid,
    updatedByUserUid,
    created,
    updated,
  } = useEditorStore(select, (a, b) => isEqual(a, b))

  const { data: fetchedQuestionnaire } = useQuestionnaire(uid)
  const { mutate: updateQuestionnaire, isPending: isUpdating } =
    useUpdateQuestionnaire()
  const { mutate: publishQuestionnaire, isPending: isPublishing } =
    usePublishQuestionnaire()
  const { getUserByUid } = useUsers()
  const [openPermissionDialog] = useModal(PermissionDialogWithOptions)

  useEffect(() => {
    if (fetchedQuestionnaire) {
      loadQuestionnaire(fetchedQuestionnaire)
    }
  }, [fetchedQuestionnaire, loadQuestionnaire])

  const handleCancel = () => {
    navigate(routes.root)
  }

  // Setup preview access checking
  const previewAccessRequestModel: PreviewAccessRequestModel = {
    uid,
    permissionPreset,
    permissionPresetUserUid,
    acl,
  }
  const { previewAccessWarningText, validationError } =
    useQuestionnairePreviewAccess(previewAccessRequestModel)

  const store = useContext(QuestionnaireEditorStoreContext)
  const handleSave = useCallback(() => {
    if (!store || !uid) {
      return
    }

    const questionnaire = store.getState().questionnaire
    updateQuestionnaire(
      {
        uid,
        title: questionnaire.title,
        sections: questionnaire.sections,
        permissionPreset: questionnaire.permissionPreset,
        permissionPresetUserUid: questionnaire.permissionPresetUserUid,
        acl: questionnaire.acl,
      },
      {
        onSuccess: () => {
          toast({
            title: t`Questionnaire saved`,
            status: 'success',
            duration: 4000,
          })
          navigate(routes.root)
        },
        onError: (error) => {
          toast({
            title: t`Error saving questionnaire`,
            description: error.message,
            status: 'error',
            duration: 4000,
          })
        },
      },
    )
  }, [navigate, toast, uid, store, updateQuestionnaire])

  const handleOpenPermissionDialog = () => {
    if (!store) {
      return
    }
    openPermissionDialog({
      initialPermissionPreset: permissionPreset,
      initialAcl: acl,
      permissionPresetUserUid: permissionPresetUserUid,
      sharingOptions: getSharingOptions(),
      onSave: (model) => {
        if (store) {
          const questionnaire = store.getState().questionnaire
          store.setState({
            questionnaire: {
              ...questionnaire,
              permissionPreset: model.permissionPreset,
              permissionPresetUserUid: model.permissionPresetUserUid,
              acl: model.acl,
            },
          })
        }
      },
    })
  }

  const [openQuestionnaireModal] = useModal(QuestionnairePreviewModal)
  const handlePreview = () => {
    if (!store) {
      return
    }
    const questionnaire = store.getState().questionnaire
    openQuestionnaireModal({
      questionnaire,
      showSubmitButton: false,
      isDisabled: false,
    })
  }

  const handlePublish = () => {
    if (!store || !uid) {
      return
    }
    const questionnaire = store.getState().questionnaire

    if (!canPublish) {
      toast({
        title: t`Cannot publish questionnaire`,
        description: t`Please fix all validation errors before publishing`,
        status: 'error',
        duration: 4000,
      })
      return
    }

    // First update the questionnaire with any changes
    updateQuestionnaire(
      {
        uid,
        title: questionnaire.title,
        sections: questionnaire.sections,
        permissionPreset: questionnaire.permissionPreset,
        permissionPresetUserUid: questionnaire.permissionPresetUserUid,
        acl: questionnaire.acl,
      },
      {
        onSuccess: () => {
          // Then publish it
          publishQuestionnaire(uid, {
            onSuccess: () => {
              toast({
                title: t`Questionnaire published`,
                status: 'success',
                duration: 4000,
              })
              navigate(routes.root)
            },
            onError: (error) => {
              toast({
                title: t`Error publishing questionnaire`,
                description: error.message,
                status: 'error',
                duration: 4000,
              })
            },
          })
        },
        onError: (error) => {
          toast({
            title: t`Error updating questionnaire`,
            description: error.message,
            status: 'error',
            duration: 4000,
          })
        },
      },
    )
  }

  return (
    <Box p={4} h="100%">
      <VStack alignItems="stretch" w="100%" mb={8}>
        <Flex alignItems="center" justifyContent="space-between" gap={8}>
          <QuestionnaireTitle />
          <Flex alignItems="center" gap={2} justifyContent="flex-start">
            <Box position="relative">
              <PermissionPresetButton
                permissionPreset={permissionPreset}
                onOpen={handleOpenPermissionDialog}
              />
              {(previewAccessWarningText || validationError) && (
                <VStack
                  position="absolute"
                  top="100%"
                  right={0}
                  alignItems="flex-end"
                  spacing={1}
                  mt={2}
                >
                  <PreviewAccessWarningText
                    warningText={previewAccessWarningText}
                  />
                  <ValidationErrorText validationError={validationError} />
                </VStack>
              )}
            </Box>

            <Button
              leftIcon={<Icon as={IoMdEye} />}
              variant="link"
              colorScheme="primary"
              onClick={handlePreview}
              mx={4}
            >
              <Trans>Preview</Trans>
            </Button>

            <QuestionnaireActionsMenu />
            <Button
              variant="outline"
              onClick={() =>
                openConfirmDialog({
                  onConfirm: handleCancel,
                })
              }
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              onClick={handleSave}
              isLoading={isUpdating && !isPublishing}
              isDisabled={isPublishing || !hasTitle}
            >
              <Trans>Save as Draft</Trans>
            </Button>
            <Button
              primary
              onClick={handlePublish}
              isDisabled={!canPublish || isUpdating}
              isLoading={isPublishing && !isUpdating}
            >
              <Trans>Publish</Trans>
            </Button>
          </Flex>
        </Flex>

        <Box alignSelf="flex-end" mt={2}>
          <VStack
            alignItems="flex-end"
            spacing={0}
            fontSize="xs"
            color="gray.600"
          >
            <Text>
              <Trans>Created by</Trans> {getUserByUid(createdByUserUid).name}{' '}
              {i18n.date(created, { dateStyle: 'long' })}
            </Text>
            <Text>
              <Trans>Updated by</Trans> {getUserByUid(updatedByUserUid).name}{' '}
              {i18n.date(updated, { dateStyle: 'long' })}
            </Text>
          </VStack>
        </Box>
      </VStack>

      <QuestionnaireEditor />
    </Box>
  )
}

export const QAQuestionnaireEditPage: FC = () => {
  const { id: userUid } = useCurrentUser()
  const { uid: organizationUid } = useOrganization()
  return (
    <EditorStoreProvider
      currentUserUid={userUid}
      organizationUid={organizationUid}
    >
      <QAQuestionnaireEditPageContent />
    </EditorStoreProvider>
  )
}
